import { Dialect } from '../dialect.js';
import { keywordDisplayForm } from '../lexer/keyword-display.js';
import type { Expression, ForStatement, IfStatement, PseudoglossaProgram, RepeatUntilStatement, Statement, SwitchStatement, WhileStatement } from '../parser/ast.js';
import { numericConstant } from '../transform/repetition.js';
import { switchToMultiple } from '../transform/selection.js';
import { printExpression, printLValue } from './printer.js';

/**
 * "Φυσική γλώσσα κατά βήματα" — Κεφάλαιο 7 του σχολικού βιβλίου ΑΕΠΠ
 * (Ενότητες 2.1-2.4.5). Μηχανική μετάφραση ΑΠΟ ήδη γραμμένη Ψευδογλώσσα
 * ΠΡΟΣ αριθμημένα βήματα φυσικής γλώσσας, με χρήση GOTO ("Πήγαινε στο βήμα
 * Ν") για τις δομές επιλογής/επανάληψης — ΑΚΡΙΒΩΣ όπως δείχνει η μεθοδολογία
 * 14.1 του βιβλίου. ΔΕΝ είναι μια νέα εκτελέσιμη γλώσσα εισόδου: η φυσική
 * γλώσσα δεν έχει αυστηρή γραμματική, άρα δεν μπορεί να γίνει parse με τον
 * ίδιο ντετερμινιστικό τρόπο — είναι ΜΟΝΟ προβολή/έξοδος πάνω σε ένα ήδη
 * έγκυρο πρόγραμμα σε Ψευδογλώσσα.
 *
 * Μόνο Ψευδογλώσσα υποστηρίζεται (όχι ΓΛΩΣΣΑ): το βιβλίο δίνει σαφή κανόνα
 * Είσοδος/Έξοδος μόνο από τα ΔΕΔΟΜΕΝΑ/ΑΠΟΤΕΛΕΣΜΑΤΑ της Ψευδογλώσσας (§14.1,
 * "Παρατηρήσεις") — για τις τυπικές δηλώσεις της ΓΛΩΣΣΑ (ΜΕΤΑΒΛΗΤΕΣ) δεν
 * δίνεται αντίστοιχος μηχανικός κανόνας, άρα δεν το μαντεύουμε.
 *
 * Το ΕΠΙΛΕΞΕ δεν καλύπτεται καθόλου από το κεφάλαιο — μετατρέπεται πρώτα σε
 * ισοδύναμη αλυσίδα ΑΝ/ΑΛΛΙΩΣ_ΑΝ/ΑΛΛΙΩΣ (ήδη υπάρχουσα, δοκιμασμένη
 * ισοδυναμία από τις μετατροπές δομών επιλογής) και μετά ακολουθείται η
 * κανονική μεθοδολογία για ΑΝ.
 */

/** `lineMap`: γραμμή πηγαίου κώδικα → αριθμός του ΠΡΩΤΟΥ "Βήμα" που αντιστοιχεί σε αυτή την εντολή — χρησιμοποιείται στο main.ts για να συγχρονίζει το highlight της τρέχουσας εντολής και στις δύο πλευρές (κώδικας/φυσική γλώσσα) κατά την εκτέλεση. */
export type NaturalLanguageResult =
  | { readonly ok: true; readonly text: string; readonly lineMap: ReadonlyMap<number, number> }
  | { readonly ok: false; readonly reason: string };

class UnsupportedConstructError extends Error {}

const NL_DIALECT = Dialect.PSEUDOGLOSSA;

function printCond(expr: Expression): string {
  return printExpression(expr, NL_DIALECT);
}

// --- Πέρασμα 1: πλήθος βημάτων (χρειάζεται ΠΡΙΝ την εκτύπωση, για να ξέρουμε τους στόχους των GOTO) --

function sumStepCount(list: readonly Statement[], preferInlineIf: boolean): number {
  return list.reduce((sum, s) => sum + stepCount(s, preferInlineIf), 0);
}

interface IfBranch {
  readonly condition: Expression;
  readonly body: readonly Statement[];
}

function ifBranches(stmt: IfStatement): readonly IfBranch[] {
  return [{ condition: stmt.condition, body: stmt.thenBranch }, ...stmt.elseIfBranches];
}

const LEAF_STATEMENT_KINDS = new Set(['AssignmentStatement', 'ReadStatement', 'WriteStatement', 'SwapStatement', 'CallStatement']);

/**
 * §14.1, "1ος τρόπος σύνταξης (Ισχύει μόνο στην ψευδογλώσσα)": ΑΝ με μία
 * συνθήκη, χωρίς ΑΛΛΙΩΣ, με ΑΚΡΙΒΩΣ μία απλή εντολή μέσα — μπορεί να γραφτεί
 * σε ΜΙΑ γραμμή χωρίς GOTO ("Αν <συνθήκη> τότε <εντολή>"), αντί για την
 * πλήρη (3-βηματη) μορφή GOTO. Ο reverse parser ήδη δέχεται αυτή τη μορφή
 * (βλ. natural-language-parser.ts) — εδώ ελέγχουμε αν ΜΠΟΡΕΙ να παραχθεί.
 */
function isInlineEligible(stmt: IfStatement): boolean {
  return stmt.elseBranch === null && stmt.elseIfBranches.length === 0 && stmt.thenBranch.length === 1 && LEAF_STATEMENT_KINDS.has(stmt.thenBranch[0]!.kind);
}

function ifStepCount(stmt: IfStatement, preferInlineIf: boolean): number {
  if (preferInlineIf && isInlineEligible(stmt)) return 1;
  const branches = ifBranches(stmt);
  const hasElse = stmt.elseBranch !== null;
  if (branches.length === 1) {
    const thenSteps = sumStepCount(branches[0]!.body, preferInlineIf);
    return hasElse ? thenSteps + sumStepCount(stmt.elseBranch!, preferInlineIf) + 2 : thenSteps + 2;
  }
  const fallthrough = hasElse ? sumStepCount(stmt.elseBranch!, preferInlineIf) + 1 : 1;
  const branchSteps = branches.reduce((sum, b) => sum + sumStepCount(b.body, preferInlineIf) + 1, 0);
  return branches.length + fallthrough + branchSteps;
}

function switchAsIf(stmt: SwitchStatement): IfStatement {
  const asIf = switchToMultiple(stmt);
  // Ο parser δεν επιτρέπει ΕΠΙΛΕΞΕ χωρίς καμία ΠΕΡΙΠΤΩΣΗ, άρα το switchToMultiple
  // (που επιστρέφει null ΜΟΝΟ όταν stmt.cases είναι άδειο) δεν αποτυγχάνει ποτέ εδώ.
  if (asIf === null) throw new UnsupportedConstructError('Η ΕΠΙΛΕΞΕ πρέπει να έχει τουλάχιστον μία ΠΕΡΙΠΤΩΣΗ.');
  return asIf;
}

function stepCount(stmt: Statement, preferInlineIf: boolean): number {
  switch (stmt.kind) {
    case 'AssignmentStatement':
    case 'ReadStatement':
    case 'WriteStatement':
    case 'SwapStatement':
    case 'CallStatement':
      return 1;
    case 'IfStatement':
      return ifStepCount(stmt, preferInlineIf);
    case 'ForStatement':
      return sumStepCount(stmt.body, preferInlineIf) + 4;
    case 'WhileStatement':
      return sumStepCount(stmt.body, preferInlineIf) + 2;
    case 'RepeatUntilStatement':
      return sumStepCount(stmt.body, preferInlineIf) + 1;
    case 'SwitchStatement':
      return ifStepCount(switchAsIf(stmt), preferInlineIf);
  }
}

// --- Πέρασμα 2: εκτύπωση — κάθε emit* ξέρει το μήκος (από το stepCount) οπότε υπολογίζει τους στόχους GOTO χωρίς δεύτερο πέρασμα backpatch ---

/** Το κείμενο μιας "απλής" (leaf) εντολής, ΧΩΡΙΣ το πρόθεμα "Βήμα Ν:" — επαναχρησιμοποιείται και για την ενσωματωμένη εντολή ενός inline "Αν...τότε". */
function renderLeafStatementText(stmt: Statement): string {
  switch (stmt.kind) {
    case 'AssignmentStatement':
      return `Θέσε ${printLValue(stmt.target, NL_DIALECT)} = ${printCond(stmt.value)}`;
    case 'ReadStatement':
      return `Διάβασε ${stmt.targets.map((t) => printLValue(t, NL_DIALECT)).join(', ')}`;
    case 'WriteStatement':
      // stmt.verb θυμάται ποιο από τα 3 συνώνυμα (Γράψε/Εκτύπωσε/Εμφάνισε) έγραψε ο μαθητής — βλ. σχόλιο στο ast.ts.
      return `${keywordDisplayForm(stmt.verb ?? 'ΓΡΑΨΕ', NL_DIALECT)} ${stmt.values.map((v) => printCond(v)).join(', ')}`;
    case 'SwapStatement':
      return `Αντιμετάθεσε ${printLValue(stmt.left, NL_DIALECT)}, ${printLValue(stmt.right, NL_DIALECT)}`;
    case 'CallStatement': {
      const argsText = stmt.args.length > 0 ? `(${stmt.args.map((a) => printCond(a)).join(', ')})` : '';
      return `Κάλεσε ${stmt.callee.name}${argsText}`;
    }
    default:
      throw new Error(`renderLeafStatementText: μη υποστηριζόμενο είδος εντολής "${stmt.kind}"`);
  }
}

function emitStatements(list: readonly Statement[], start: number, lines: string[], lineMap: Map<number, number>, preferInlineIf: boolean): number {
  let step = start;
  for (const stmt of list) step = emitStatement(stmt, step, lines, lineMap, preferInlineIf);
  return step;
}

function emitStatement(stmt: Statement, step: number, lines: string[], lineMap: Map<number, number>, preferInlineIf: boolean): number {
  lineMap.set(stmt.line, step);
  switch (stmt.kind) {
    case 'AssignmentStatement':
    case 'ReadStatement':
    case 'WriteStatement':
    case 'SwapStatement':
    case 'CallStatement':
      lines.push(`Βήμα ${step}: ${renderLeafStatementText(stmt)}`);
      return step + 1;
    case 'IfStatement':
      return emitIf(stmt, step, lines, lineMap, preferInlineIf);
    case 'ForStatement':
      return emitFor(stmt, step, lines, lineMap, preferInlineIf);
    case 'WhileStatement':
      return emitWhile(stmt, step, lines, lineMap, preferInlineIf);
    case 'RepeatUntilStatement':
      return emitRepeatUntil(stmt, step, lines, lineMap, preferInlineIf);
    case 'SwitchStatement':
      return emitIf(switchAsIf(stmt), step, lines, lineMap, preferInlineIf);
  }
}

/** §14.1, ερωτήσεις 2-4: ΑΝ (με 1 συνθήκη, με/χωρίς ΑΛΛΙΩΣ) έχει την πιο οικονομική μορφή· με 2+ συνθήκες (ΑΛΛΙΩΣ_ΑΝ) γενικεύεται σε αλυσίδα ελέγχων. */
function emitIf(stmt: IfStatement, K: number, lines: string[], lineMap: Map<number, number>, preferInlineIf: boolean): number {
  if (preferInlineIf && isInlineEligible(stmt)) {
    lines.push(`Βήμα ${K}: Αν ${printCond(stmt.condition)} τότε ${renderLeafStatementText(stmt.thenBranch[0]!)}`);
    return K + 1;
  }

  const branches = ifBranches(stmt);
  const hasElse = stmt.elseBranch !== null;
  const elseBody = stmt.elseBranch ?? [];

  if (branches.length === 1) {
    const branch = branches[0]!;
    const thenSteps = sumStepCount(branch.body, preferInlineIf);
    const cond = printCond(branch.condition);
    if (hasElse) {
      const thenStart = K + 1;
      const elseStart = K + thenSteps + 2;
      const end = elseStart + sumStepCount(elseBody, preferInlineIf);
      lines.push(`Βήμα ${K}: Αν ${cond} τότε πήγαινε στο βήμα ${thenStart} αλλιώς πήγαινε στο βήμα ${elseStart}`);
      emitStatements(branch.body, thenStart, lines, lineMap, preferInlineIf);
      lines.push(`Βήμα ${K + thenSteps + 1}: Πήγαινε στο βήμα ${end}`);
      emitStatements(elseBody, elseStart, lines, lineMap, preferInlineIf);
      return end;
    }
    const bodyStart = K + 2;
    const end = bodyStart + thenSteps;
    lines.push(`Βήμα ${K}: Αν ${cond} τότε πήγαινε στο βήμα ${bodyStart}`);
    lines.push(`Βήμα ${K + 1}: Πήγαινε στο βήμα ${end}`);
    emitStatements(branch.body, bodyStart, lines, lineMap, preferInlineIf);
    return end;
  }

  const C = branches.length;
  const fallthroughStart = K + C;
  const fallthroughLen = hasElse ? sumStepCount(elseBody, preferInlineIf) + 1 : 1;
  const branchStarts: number[] = [];
  let cursor = fallthroughStart + fallthroughLen;
  for (const b of branches) {
    branchStarts.push(cursor);
    cursor += sumStepCount(b.body, preferInlineIf) + 1;
  }
  const end = cursor;

  for (let i = 0; i < C; i++) {
    lines.push(`Βήμα ${K + i}: Αν ${printCond(branches[i]!.condition)} τότε πήγαινε στο βήμα ${branchStarts[i]}`);
  }

  if (hasElse) {
    emitStatements(elseBody, fallthroughStart, lines, lineMap, preferInlineIf);
    lines.push(`Βήμα ${fallthroughStart + sumStepCount(elseBody, preferInlineIf)}: Πήγαινε στο βήμα ${end}`);
  } else {
    lines.push(`Βήμα ${fallthroughStart}: Πήγαινε στο βήμα ${end}`);
  }

  for (let i = 0; i < C; i++) {
    const bodyEnd = emitStatements(branches[i]!.body, branchStarts[i]!, lines, lineMap, preferInlineIf);
    lines.push(`Βήμα ${bodyEnd}: Πήγαινε στο βήμα ${end}`);
  }

  return end;
}

/** §14.1, ερώτηση 5. */
function emitWhile(stmt: WhileStatement, K: number, lines: string[], lineMap: Map<number, number>, preferInlineIf: boolean): number {
  const bodyStart = K + 1;
  const backStep = bodyStart + sumStepCount(stmt.body, preferInlineIf);
  const end = backStep + 1;
  lines.push(`Βήμα ${K}: Αν ${printCond(stmt.condition)} τότε πήγαινε στο βήμα ${bodyStart} αλλιώς πήγαινε στο βήμα ${end}`);
  emitStatements(stmt.body, bodyStart, lines, lineMap, preferInlineIf);
  lines.push(`Βήμα ${backStep}: Πήγαινε στο βήμα ${K}`);
  return end;
}

/** §14.1, ερώτηση 6. */
function emitRepeatUntil(stmt: RepeatUntilStatement, K: number, lines: string[], lineMap: Map<number, number>, preferInlineIf: boolean): number {
  const condStep = K + sumStepCount(stmt.body, preferInlineIf);
  const end = condStep + 1;
  emitStatements(stmt.body, K, lines, lineMap, preferInlineIf);
  lines.push(`Βήμα ${condStep}: Αν ${printCond(stmt.condition)} τότε πήγαινε στο βήμα ${end} αλλιώς πήγαινε στο βήμα ${K}`);
  return end;
}

/**
 * §14.1, ερώτηση 7 — το βιβλίο δείχνει μόνο την αύξουσα περίπτωση (<=). Για
 * φθίνον βήμα (ΜΕ_ΒΗΜΑ αρνητικό) χρειάζεται >=, με την ΙΔΙΑ λογική που ήδη
 * εφαρμόζει η μετατροπή ΓΙΑ→ΟΣΟ (βλ. `transform/repetition.ts`) — αλλιώς η
 * σύγκριση θα ήταν λάθος κατεύθυνσης. Όταν το βήμα δεν είναι αποδεδειγμένα
 * σταθερό, δεν μαντεύουμε: αρνούμαστε τη μετατροπή.
 */
function emitFor(stmt: ForStatement, K: number, lines: string[], lineMap: Map<number, number>, preferInlineIf: boolean): number {
  const stepValue = stmt.step === null ? 1 : numericConstant(stmt.step);
  if (stepValue === null) {
    throw new UnsupportedConstructError('Το ΜΕ_ΒΗΜΑ ενός ΓΙΑ πρέπει να είναι σταθερός αριθμός για μετατροπή σε φυσική γλώσσα κατά βήματα.');
  }
  if (stepValue === 0) {
    throw new UnsupportedConstructError('Το ΜΕ_ΒΗΜΑ ενός ΓΙΑ δεν μπορεί να είναι 0.');
  }
  const varName = stmt.variable.name;
  const stepText = stmt.step !== null ? printCond(stmt.step) : '1';
  const comparisonOp = stepValue > 0 ? '<=' : '>=';

  const condStep = K + 1;
  const bodyStart = K + 2;
  const incStep = bodyStart + sumStepCount(stmt.body, preferInlineIf);
  const backStep = incStep + 1;
  const end = backStep + 1;

  lines.push(`Βήμα ${K}: Θέσε ${varName} = ${printCond(stmt.from)}`);
  lines.push(`Βήμα ${condStep}: Αν ${varName} ${comparisonOp} ${printCond(stmt.to)} τότε πήγαινε στο βήμα ${bodyStart} αλλιώς πήγαινε στο βήμα ${end}`);
  emitStatements(stmt.body, bodyStart, lines, lineMap, preferInlineIf);
  lines.push(`Βήμα ${incStep}: Θέσε ${varName} = ${varName} + ${stepText}`);
  lines.push(`Βήμα ${backStep}: Πήγαινε στο βήμα ${condStep}`);
  return end;
}

/**
 * Μόνο τα "Βήμα Ν: ..." — χωρίς κεφαλίδα (Αλγόριθμος/Είσοδος/Έξοδος). Για
 * "Τμήμα αλγορίθμου" (βλ. `parseFragment` στο main.ts): ένα τμήμα δεν έχει
 * όνομα ούτε Δεδομένα/Αποτελέσματα, οπότε ξεκινάει κατευθείαν με "Βήμα 1".
 */
export function printStatementsAsNaturalLanguageSteps(statements: readonly Statement[], preferInlineIf = false): NaturalLanguageResult {
  try {
    const lines: string[] = [];
    const lineMap = new Map<number, number>();
    emitStatements(statements, 1, lines, lineMap, preferInlineIf);
    return { ok: true, text: lines.join('\n'), lineMap };
  } catch (err) {
    if (err instanceof UnsupportedConstructError) return { ok: false, reason: err.message };
    throw err;
  }
}

/**
 * Ονόματα μεταβλητών με τη σειρά της ΠΡΩΤΗΣ εμφάνισής τους στο πρόγραμμα (σε
 * στόχο ΔΙΑΒΑΣΕ, τιμή ΓΡΑΨΕ, στόχο ανάθεσης/ΑΝΤΙΜΕΤΑΘΕΣΕ, μετρητή ΓΙΑ) — μαζί
 * με τα σύνολα "διαβασμένων"/"τυπωμένων" ονομάτων. Ίδιο σκεπτικό με το
 * `collectReadWrittenVarNames` του natural-language-parser.ts (αντίστροφη
 * κατεύθυνση) — χρησιμοποιείται εδώ για να συμπληρωθούν αυτόματα οι γραμμές
 * "Είσοδος:"/"Έξοδος:" όταν το πρόγραμμα ΔΕΝ έχει ρητά ΔΕΔΟΜΕΝΑ/ΑΠΟΤΕΛΕΣΜΑΤΑ
 * (βλ. `printProgramAsNaturalLanguageSteps`), με ΑΚΡΙΒΩΣ τον ίδιο κανόνα που
 * ήδη εφαρμόζει η αντίστροφη μετατροπή (φυσική→κώδικας, §14.1
 * "Παρατηρήσεις"): μια μεταβλητή που διαβάζεται/τυπώνεται ρητά σε Βήμα
 * μετράει κανονικά ως Είσοδος/Έξοδος — δεν χρειάζεται ΚΑΙ ρητό ΔΕΔΟΜΕΝΑ/
 * ΑΠΟΤΕΛΕΣΜΑΤΑ.
 */
function collectVariableUsage(statements: readonly Statement[]): {
  readonly appearanceOrder: readonly string[];
  readonly read: ReadonlySet<string>;
  readonly written: ReadonlySet<string>;
} {
  const read = new Set<string>();
  const written = new Set<string>();
  const seen = new Set<string>();
  const appearanceOrder: string[] = [];
  const note = (name: string): void => {
    if (!seen.has(name)) {
      seen.add(name);
      appearanceOrder.push(name);
    }
  };

  const visitAll = (stmts: readonly Statement[]): void => {
    for (const stmt of stmts) visitOne(stmt);
  };
  const visitOne = (stmt: Statement): void => {
    switch (stmt.kind) {
      case 'ReadStatement':
        for (const target of stmt.targets) {
          if (target.kind === 'Identifier') {
            read.add(target.name);
            note(target.name);
          }
        }
        break;
      case 'WriteStatement':
        for (const value of stmt.values) {
          if (value.kind === 'Identifier') {
            written.add(value.name);
            note(value.name);
          }
        }
        break;
      case 'AssignmentStatement':
        if (stmt.target.kind === 'Identifier') note(stmt.target.name);
        break;
      case 'SwapStatement':
        if (stmt.left.kind === 'Identifier') note(stmt.left.name);
        if (stmt.right.kind === 'Identifier') note(stmt.right.name);
        break;
      case 'ForStatement':
        note(stmt.variable.name);
        visitAll(stmt.body);
        break;
      case 'WhileStatement':
      case 'RepeatUntilStatement':
        visitAll(stmt.body);
        break;
      case 'IfStatement':
        visitAll(stmt.thenBranch);
        for (const branch of stmt.elseIfBranches) visitAll(branch.body);
        if (stmt.elseBranch !== null) visitAll(stmt.elseBranch);
        break;
      case 'SwitchStatement':
        for (const switchCase of stmt.cases) visitAll(switchCase.body);
        if (stmt.elseBody !== null) visitAll(stmt.elseBody);
        break;
      default:
        break;
    }
  };

  visitAll(statements);
  return { appearanceOrder, read, written };
}

/**
 * §14.1 (μεθοδολογία): "Αλγόριθμος Όνομα" γράφεται όπως είναι, τα ΔΕΔΟΜΕΝΑ/
 * ΑΠΟΤΕΛΕΣΜΑΤΑ γίνονται γραμμές Είσοδος/Έξοδος (οι ίδιες οι λέξεις-κλειδιά
 * ΔΕΔΟΜΕΝΑ/ΑΠΟΤΕΛΕΣΜΑΤΑ ΔΕΝ γράφονται στη φυσική γλώσσα — μόνο οι μεταβλητές
 * τους), και κάθε εντολή παίρνει ένα "Βήμα Ν:" μπροστά. Το "Τέλος Όνομα" δεν
 * γράφεται καθόλου. Οι γραμμές "Είσοδος:"/"Έξοδος:" είναι ΥΠΟΧΡΕΩΤΙΚΕΣ σε
 * πλήρη αλγόριθμο (ακόμα κι όταν η λίστα είναι άδεια) — μόνο σε "Τμήμα
 * αλγορίθμου" (βλ. `printStatementsAsNaturalLanguageSteps`) παραλείπονται.
 *
 * @param preferInlineIf Όταν true, μια απλή ΑΝ (χωρίς ΑΛΛΙΩΣ, με μία εντολή
 *   μέσα) τυπώνεται στη σύντομη μορφή μίας γραμμής αντί για την πλήρη GOTO
 *   (βλ. `isInlineEligible`) — false από προεπιλογή, ώστε το ρητό κουμπί
 *   "Μετάτρεψε →" να δείχνει πάντα την πλήρη διδακτική μεθοδολογία GOTO. Το
 *   main.ts το ενεργοποιεί ΜΟΝΟ στο εσωτερικό "re-sync" μετά από φυσική→
 *   κώδικας, ώστε να ΜΗΝ ξαναγράφεται άσκοπα η ήδη-έγκυρη σύντομη μορφή που
 *   έγραψε ο μαθητής.
 */
export function printProgramAsNaturalLanguageSteps(program: PseudoglossaProgram, preferInlineIf = false): NaturalLanguageResult {
  const bodyResult = printStatementsAsNaturalLanguageSteps(program.body, preferInlineIf);
  if (!bodyResult.ok) return bodyResult;
  const lines: string[] = [`Αλγόριθμος ${program.name}`];
  // Ρητό ΔΕΔΟΜΕΝΑ/ΑΠΟΤΕΛΕΣΜΑΤΑ (ο μαθητής το έγραψε ρητά στην Ψευδογλώσσα)
  // ΣΥΝΔΥΑΖΕΤΑΙ με ό,τι διαβάζεται/τυπώνεται ρητά στο σώμα (βλ.
  // `collectVariableUsage`) — ένα πρόγραμμα μπορεί νόμιμα να αναμειγνύει τους
  // δύο τρόπους (μερικές μεταβλητές μέσω ΔΕΔΟΜΕΝΑ, άλλες μέσω ρητού Διάβασε).
  const { appearanceOrder, read, written } = collectVariableUsage(program.body);
  const inputVars = [
    ...program.dataVariables,
    ...appearanceOrder.filter((name) => read.has(name) && !program.dataVariables.includes(name)),
  ];
  const outputVars = [
    ...program.resultVariables,
    ...appearanceOrder.filter((name) => written.has(name) && !program.resultVariables.includes(name)),
  ];
  lines.push(inputVars.length > 0 ? `Είσοδος: ${inputVars.join(', ')}` : 'Είσοδος:');
  lines.push(outputVars.length > 0 ? `Έξοδος: ${outputVars.join(', ')}` : 'Έξοδος:');
  if (bodyResult.text.length > 0) lines.push(bodyResult.text);
  return { ok: true, text: lines.join('\n'), lineMap: bodyResult.lineMap };
}
